import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight } from "lucide-react";
import React from "react";

import {
  bucketTone,
  BUCKET_EXPIRED,
  getStockItems,
  STOCK_ITEMS_QUERY,
  type StockBatch,
  type StockItem,
  type StockPairRef,
} from "@/api/warehouse_dashboard";
import { fmtDate, fmtPercent, ruNum } from "@/lib/format";
import { LoadState } from "@/lib/scan-ui/screen";
import { cn } from "@/lib/utils";

import { DashboardWarnings } from "./DashboardWarnings";
import { fmtCost, NUM, ShareCell, UNITS } from "./stock-cells";
import { DASHBOARD_STALE_MS } from "./tabs";

/** Больше стольких позиций — появляется поиск по артикулу/названию. */
export const STOCK_SEARCH_FROM = 50;
/** Сколько строк показать до «Показать все». */
export const STOCK_ROWS_LIMIT = 100;

/** Линии вложенности: склад (толстая, как в строке склада) → позиция → партия. */
const LINE_WH = "ml-1.5 border-l-2 border-slate-300";
const LINE_ITEM = "ml-2 border-l border-slate-300";
const LINE_BATCH = "ml-5 border-l border-slate-200";

/** Строка на всю ширину таблицы внутри раскрытого склада (загрузка, плашка, поиск). */
function WideRow({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <tr className="border-b bg-white">
      <td colSpan={5} className="py-0 pl-3 pr-3">
        <div className={cn(LINE_WH, "py-1 pl-3")}>{children}</div>
      </td>
    </tr>
  );
}

/**
 * Позиции раскрытого склада: запрос по паре (ключ react-query — пара,
 * `staleTime` как у сводки), `LoadState` внутри строки. Ответ рисуется как
 * есть: порядок позиций (₽ убыв.) и партий (годен до возр.) — бэкенда,
 * `warnings` — плашкой. Доля позиции — от ₽ склада из ЭТОГО ответа.
 */
export function StockItemRows({ pair }: { pair: StockPairRef }): React.ReactElement {
  const q = useQuery({
    queryKey: [STOCK_ITEMS_QUERY, pair.organization_ref, pair.warehouse_ref],
    queryFn: () => getStockItems(pair),
    staleTime: DASHBOARD_STALE_MS,
  });
  const [search, setSearch] = React.useState("");
  const [showAll, setShowAll] = React.useState(false);
  // Раскрытые позиции — здесь, а не в строке: «Раскрыть все партии» склада
  // управляет ими разом.
  const [openItems, setOpenItems] = React.useState<Set<string>>(new Set());
  const toggleItem = (key: string) =>
    setOpenItems((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  if (q.isLoading || q.error || !q.data) {
    return (
      <WideRow>
        <LoadState
          isLoading={q.isLoading}
          error={q.error}
          onRetry={() => void q.refetch()}
          className="flex items-center gap-3 py-1 text-left text-xs [&_svg]:size-4"
        />
      </WideRow>
    );
  }
  const data = q.data;
  const needle = search.trim().toLowerCase();
  const found = needle
    ? data.items.filter(
        (i) => i.article.toLowerCase().includes(needle) || i.name.toLowerCase().includes(needle),
      )
    : data.items;
  const shown = showAll ? found : found.slice(0, STOCK_ROWS_LIMIT);
  // Кнопка раскрывает партии ВИДИМЫХ позиций: скрытые за «Показать все»
  // раскрывать некуда, и подсказка это говорит.
  const expandable = shown.filter((i) => i.batches.length > 0).map(itemKey);
  const allOpen = expandable.length > 0 && expandable.every((k) => openItems.has(k));
  const toggleAll = () =>
    setOpenItems((prev) => {
      const next = new Set(prev);
      for (const k of expandable) {
        if (allOpen) next.delete(k);
        else next.add(k);
      }
      return next;
    });
  const hasBatches = data.items.some((i) => i.batches.length > 0);

  return (
    <>
      {data.warnings.length > 0 && (
        <WideRow>
          <DashboardWarnings warnings={data.warnings} />
        </WideRow>
      )}
      {(data.items.length > STOCK_SEARCH_FROM || hasBatches) && (
        <WideRow>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {data.items.length > STOCK_SEARCH_FROM && (
              <>
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Артикул или название"
                  aria-label="Поиск по позициям склада"
                  className="h-7 w-64 max-w-full rounded border border-slate-300 bg-white px-2 text-[13px] outline-none focus:border-slate-500"
                />
                <span className="text-xs text-muted-foreground tabular-nums">
                  {needle
                    ? `найдено ${ruNum.format(found.length)} из ${ruNum.format(data.items.length)}`
                    : `${ruNum.format(data.items.length)} поз.`}
                </span>
              </>
            )}
            {expandable.length > 0 && (
              <button
                type="button"
                onClick={toggleAll}
                className="text-xs font-medium text-sky-700 hover:underline"
              >
                {allOpen ? "Свернуть партии" : "Раскрыть все партии"}
              </button>
            )}
            {expandable.length > 0 && !showAll && found.length > STOCK_ROWS_LIMIT && (
              <span className="text-xs text-muted-foreground">
                только у показанных {ruNum.format(STOCK_ROWS_LIMIT)} из{" "}
                {ruNum.format(found.length)} — остальные после «Показать все»
              </span>
            )}
          </div>
        </WideRow>
      )}
      {data.items.length === 0 && (
        <WideRow>
          <span className="text-xs text-muted-foreground">Позиций нет</span>
        </WideRow>
      )}
      {shown.map((item) => (
        <ItemRows
          key={itemKey(item)}
          item={item}
          total={data.total_cost_rub}
          open={openItems.has(itemKey(item))}
          onToggle={() => toggleItem(itemKey(item))}
        />
      ))}
      {!showAll && found.length > STOCK_ROWS_LIMIT && (
        <WideRow>
          <button
            type="button"
            onClick={() => setShowAll(true)}
            className="text-xs font-medium text-sky-700 hover:underline"
          >
            Показать все {ruNum.format(found.length)} (показано {ruNum.format(STOCK_ROWS_LIMIT)})
          </button>
        </WideRow>
      )}
    </>
  );
}

/** Ключ позиции склада (и React-ключ строки, и ключ раскрытия). */
function itemKey(item: StockItem): string {
  return `${item.nomenclature_ref}|${item.characteristic_ref}`;
}

function ItemRows({
  item,
  total,
  open,
  onToggle,
}: {
  item: StockItem;
  total: number | null;
  open: boolean;
  onToggle: () => void;
}): React.ReactElement {
  const expandable = item.batches.length > 0;
  const title = [item.article, item.name, item.characteristic].filter(Boolean).join(" · ");
  const label = (
    <>
      {item.article && (
        <span className="shrink-0 tabular-nums text-muted-foreground">{item.article} ·</span>
      )}
      <span className="min-w-0 truncate">{item.name}</span>
      {item.characteristic && (
        <span
          className="max-w-[40%] shrink-0 truncate whitespace-nowrap rounded border border-slate-200 bg-white px-1 text-[11px] leading-4 text-slate-600"
          title={`Характеристика: ${item.characteristic}`}
        >
          {item.characteristic}
        </span>
      )}
    </>
  );
  return (
    <>
      <tr className={cn("border-b border-slate-100 bg-white hover:bg-sky-50/60", open && "bg-sky-50/40")}>
        <td colSpan={2} className="py-0 pl-3 pr-2">
          <div className={cn(LINE_WH, "pl-0")}>
            <div className={cn(LINE_ITEM, "pl-2")}>
              {expandable ? (
                <button
                  type="button"
                  aria-expanded={open}
                  onClick={onToggle}
                  title={title}
                  className="flex w-full min-w-0 items-center gap-1.5 py-0.5 text-left"
                >
                  {open ? (
                    <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
                  )}
                  {label}
                </button>
              ) : (
                // Только нулевая партия — раскрывать нечего; место шеврона
                // сохраняем, чтобы названия стояли в одну колонку.
                <div className="flex min-w-0 items-center gap-1.5 py-0.5 pl-5" title={title}>
                  {label}
                </div>
              )}
            </div>
          </div>
        </td>
        <td className={cn(NUM, "py-0.5")}>{fmtCost(item.cost_rub)}</td>
        <td className={cn(UNITS, "py-0.5")}>{ruNum.format(item.qty)}</td>
        <ShareCell cost={item.cost_rub} total={total} />
      </tr>
      {open &&
        item.batches.map((b) => <BatchRow key={b.batch_ref || "—"} batch={b} />)}
    </>
  );
}

/** Пилюля ОСГ цветом корзины (`bucketTone`, по ключу): процент, «просрочено»
 *  или «нет срока» — корзина бэкенда, фронт её не выводит из дат. */
function ShelfLifePill({ batch }: { batch: StockBatch }): React.ReactElement {
  const tone = bucketTone(batch.bucket);
  const text =
    batch.shelf_life_pct !== null
      ? fmtPercent(batch.shelf_life_pct)
      : batch.bucket === BUCKET_EXPIRED
        ? "просрочено"
        : "нет срока";
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-slate-100 px-1.5 text-[11px] leading-4 tabular-nums text-slate-700">
      <span className={cn("size-1.5 rounded-full", tone.dot)} />
      {text}
    </span>
  );
}

function BatchRow({ batch }: { batch: StockBatch }): React.ReactElement {
  const parts = [
    batch.batch_ref ? `№ ${batch.batch}` : batch.batch,
    batch.produced && `произв. ${fmtDate(batch.produced)}`,
    batch.expires && `годен до ${fmtDate(batch.expires)}`,
  ].filter(Boolean);
  const text = parts.join(" · ");
  return (
    <tr className="border-b border-slate-100 bg-white text-muted-foreground">
      <td colSpan={2} className="py-0 pl-3 pr-2">
        <div className={LINE_WH}>
          <div className={LINE_ITEM}>
            <div
              className={cn(LINE_BATCH, "flex min-w-0 items-center gap-1.5 py-0.5 pl-2")}
              title={text}
            >
              <span className="min-w-0 truncate tabular-nums">{text}</span>
              <ShelfLifePill batch={batch} />
            </div>
          </div>
        </div>
      </td>
      <td className={cn(NUM, "py-0.5")}>{fmtCost(batch.cost_rub)}</td>
      <td className={cn(UNITS, "py-0.5")}>{ruNum.format(batch.qty)}</td>
      <td />
    </tr>
  );
}
